import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchNotificationHistory, markNotificationAsRead } from '../../services/api';
import { Container, Table, Badge, Button, Card, Modal, Form, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const NotificationHistory = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const [resolucaoTexto, setResolucaoTexto] = useState('');
    const [alert, setAlert] = useState(null);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [busca, setBusca] = useState('');

    const { data: historyData, isLoading, refetch } = useQuery({
        queryKey: ['notification-history'],
        queryFn: fetchNotificationHistory
    });

    const notifications = historyData?.data || [];

    // Filtrar notificações
    const notificacoesFiltradas = notifications.filter(notif => {
        const matchStatus = !filtroStatus || notif.status === filtroStatus;
        const matchBusca = !busca ||
            notif.codigo_produto?.toLowerCase().includes(busca.toLowerCase()) ||
            notif.observacao?.toLowerCase().includes(busca.toLowerCase()) ||
            notif.criado_por?.toLowerCase().includes(busca.toLowerCase());
        return matchStatus && matchBusca;
    });

    // Mutation para resolver observação
    const resolveMutation = useMutation({
        mutationFn: async ({ id, resolucao }) => {
            const response = await api.put(`/api/tasks/${id}/status`, {
                status: 'concluido',
                resolucao_observacao: resolucao
            });
            return response.data;
        },
        onSuccess: () => {
            setAlert({ type: 'success', message: '✅ Observação marcada como resolvida!' });
            setShowResolveModal(false);
            setSelectedNotif(null);
            setResolucaoTexto('');
            queryClient.invalidateQueries(['notification-history']);
            queryClient.invalidateQueries(['user-notifications']);
            setTimeout(() => setAlert(null), 3000);
        },
        onError: () => {
            setAlert({ type: 'danger', message: 'Erro ao resolver observação' });
        }
    });

    const handleOpenItem = (notif) => {
        const codigo = notif.codigo_produto;
        const origem = notif.origem_produto || 'ecommerce';

        // Marcar como lida
        markNotificationAsRead(notif.id);

        // Determinar rota baseada na origem
        if (origem.includes('conferencia_limeira')) {
            navigate('/limeira/conferencia');
        } else if (origem.includes('ecommerce')) {
            navigate(`/ecommerce?search=${codigo}&action=open_obs`);
        } else if (origem.includes('caxias') || origem.includes('004')) {
            navigate(`/loja-fisica/caxias?search=${codigo}&action=open_obs`);
        } else if (origem.includes('centro') || origem.includes('001')) {
            navigate(`/loja-fisica/centro?search=${codigo}&action=open_obs`);
        } else {
            navigate(`/ecommerce?search=${codigo}&action=open_obs`);
        }
    };

    const handleOpenResolveModal = (notif) => {
        setSelectedNotif(notif);
        setResolucaoTexto('');
        setShowResolveModal(true);
    };

    const handleResolve = () => {
        if (!selectedNotif) return;
        resolveMutation.mutate({
            id: selectedNotif.id,
            resolucao: resolucaoTexto
        });
    };

    const getOrigemLabel = (origem) => {
        if (!origem) return 'N/A';
        if (origem.includes('conferencia_limeira')) return '📦 Conferência';
        if (origem.includes('ecommerce')) return '🛒 E-commerce';
        if (origem.includes('caxias') || origem.includes('004')) return '🏬 Caxias';
        if (origem.includes('centro') || origem.includes('001')) return '🏬 Centro';
        return origem;
    };

    const getOrigemBadgeColor = (origem) => {
        if (!origem) return 'secondary';
        if (origem.includes('conferencia_limeira')) return 'info';
        if (origem.includes('ecommerce')) return 'primary';
        return 'warning';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Container fluid className="p-4">
            <h2 className="mb-4">📋 Histórico de Notificações</h2>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <span className="fw-bold">🔔 Todas as Notificações</span>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex gap-2 justify-content-end">
                                <InputGroup size="sm" style={{ width: '200px' }}>
                                    <InputGroup.Text>🔍</InputGroup.Text>
                                    <Form.Control
                                        placeholder="Buscar..."
                                        value={busca}
                                        onChange={(e) => setBusca(e.target.value)}
                                    />
                                </InputGroup>
                                <Form.Select
                                    size="sm"
                                    style={{ width: '150px' }}
                                    value={filtroStatus}
                                    onChange={(e) => setFiltroStatus(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="pendente">⏳ Pendentes</option>
                                    <option value="concluido">✅ Resolvidos</option>
                                </Form.Select>
                                <Badge bg="light" text="dark" className="d-flex align-items-center">
                                    {notificacoesFiltradas.length} itens
                                </Badge>
                            </div>
                        </Col>
                    </Row>
                </Card.Header>
                <Card.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : notificacoesFiltradas.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <div style={{ fontSize: '4rem' }}>📭</div>
                            <h5 className="mt-3">Nenhuma notificação encontrada</h5>
                            <p>Não há notificações com os filtros selecionados.</p>
                        </div>
                    ) : (
                        <Table responsive hover className="align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '140px' }}>Data</th>
                                    <th>Origem</th>
                                    <th>Código</th>
                                    <th>Status</th>
                                    <th>Criado Por</th>
                                    <th>Responsável</th>
                                    <th style={{ maxWidth: '250px' }}>Observação</th>
                                    <th style={{ maxWidth: '200px' }}>Resolução</th>
                                    <th style={{ width: '150px' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notificacoesFiltradas.map(notif => (
                                    <tr key={notif.id} className={notif.status === 'pendente' ? 'table-warning' : ''}>
                                        <td className="small">{formatDate(notif.data_criacao)}</td>
                                        <td>
                                            <Badge bg={getOrigemBadgeColor(notif.origem_produto)}>
                                                {getOrigemLabel(notif.origem_produto)}
                                            </Badge>
                                        </td>
                                        <td className="fw-bold">{notif.codigo_produto}</td>
                                        <td>
                                            {notif.status === 'concluido' ?
                                                <Badge bg="success">✅ Resolvido</Badge> :
                                                <Badge bg="warning" text="dark">⏳ Pendente</Badge>
                                            }
                                        </td>
                                        <td>{notif.criado_por || '-'}</td>
                                        <td>{notif.responsavel || <span className="text-muted">Todos</span>}</td>
                                        <td style={{ maxWidth: '250px' }} className="text-truncate" title={notif.observacao}>
                                            {notif.observacao}
                                        </td>
                                        <td style={{ maxWidth: '200px' }} className="text-muted small text-truncate" title={notif.resolucao_observacao}>
                                            {notif.resolucao_observacao || '-'}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => handleOpenItem(notif)}
                                                    title="Ver item"
                                                >
                                                    👁️
                                                </Button>
                                                {notif.status !== 'concluido' && (
                                                    <Button
                                                        size="sm"
                                                        variant="success"
                                                        onClick={() => handleOpenResolveModal(notif)}
                                                        title="Marcar como resolvido"
                                                    >
                                                        ✅
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Modal de Resolução */}
            <Modal show={showResolveModal} onHide={() => setShowResolveModal(false)} centered>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>✅ Resolver Observação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedNotif && (
                        <>
                            <div className="mb-3 p-3 bg-light rounded">
                                <small className="text-muted">Observação original:</small>
                                <p className="mb-0 fw-bold">{selectedNotif.observacao}</p>
                                <small className="text-muted">
                                    Por {selectedNotif.criado_por} em {formatDate(selectedNotif.data_criacao)}
                                </small>
                            </div>

                            <Form.Group>
                                <Form.Label>Observação de resolução (opcional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder="Ex: Problema resolvido, produto entregue..."
                                    value={resolucaoTexto}
                                    onChange={(e) => setResolucaoTexto(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowResolveModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleResolve}
                        disabled={resolveMutation.isPending}
                    >
                        {resolveMutation.isPending ? 'Resolvendo...' : '✅ Marcar como Resolvido'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default NotificationHistory;
